import { useEffect, useState } from "react";
import { useSWRConfig } from "swr";
import type { ArtifactPreview } from "~/services/artifacts";
import { urlFromPreview } from "./inlineViewerPreview";

// The preview service signs a new `content.url` on every preview request, and
// SWR requests previews again on focus and remount, so the URL changes while
// the artifact does not. A viewer keyed on it would reload, and lose the
// user's view, each time. This holds the URL captured for an artifact version
// until it expires, then revalidates the previews and moves to the fresh one,
// which matters for viewers that keep range-reading (Juicebox) after loading.
export function useInlineViewerUrl(preview: ArtifactPreview): string | null {
  const latest = urlFromPreview(preview);
  const artifactKey = `${preview.artifact_id}:${preview.version}`;
  const [held, setHeld] = useState({ artifactKey, url: latest });
  const [now, setNow] = useState(() => Date.now());
  const { mutate } = useSWRConfig();

  const heldExpired = isExpired(held.url, now);
  const latestUsable = latest !== null && latest !== held.url && !isExpired(latest, now);
  // Adjusting state during render: React's pattern for following an input
  // that changed, without an extra effect pass.
  if (held.artifactKey !== artifactKey || (heldExpired && latestUsable)) {
    setHeld({ artifactKey, url: latest });
  }

  useEffect(() => {
    const expiresAt = expiryOf(held.url);
    if (expiresAt === null) {
      return;
    }
    const timer = setTimeout(
      () => setNow(Date.now()),
      Math.min(Math.max(expiresAt - Date.now(), 0), MAX_TIMEOUT_MS)
    );
    return () => clearTimeout(timer);
  }, [held.url]);

  useEffect(() => {
    if (heldExpired) {
      void mutate(isArtifactPreviewKey);
    }
  }, [heldExpired, mutate]);

  return held.url;
}

// setTimeout stores its delay in a signed 32-bit integer.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

function isArtifactPreviewKey(key: unknown): boolean {
  return (
    typeof key === "string" && (key.includes("/artifacts/previews?") || key.endsWith("/preview"))
  );
}

function isExpired(url: string | null, now: number): boolean {
  const expiresAt = expiryOf(url);
  return expiresAt !== null && now >= expiresAt;
}

// Expiry of an S3 SigV4 presigned URL (`X-Amz-Date` plus `X-Amz-Expires`
// seconds), or null for a URL that carries no such signature.
function expiryOf(url: string | null): number | null {
  if (url === null) {
    return null;
  }
  const params = new URL(url).searchParams;
  const date = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(
    params.get("X-Amz-Date") ?? ""
  );
  const expiresIn = Number(params.get("X-Amz-Expires"));
  if (!date || !Number.isFinite(expiresIn) || expiresIn <= 0) {
    return null;
  }
  const [, year, month, day, hour, minute, second] = date.map(Number);
  return Date.UTC(year, month - 1, day, hour, minute, second) + expiresIn * 1000;
}
